import { Button, Modal, Radio, Select, TextInput } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import ClientApi from '../../Api/ClientApi';
import {
  fetchEquipmentTypes,
  fetchInventoryTypes,
} from '../../Actions/CommonFields';
import { ClientLocationAsset } from '../../Models/ClientLocationAsset';

export default function ClientLocationAssetModal({
  show,
  setShow,
  getDeliveryItems,
  getAssetsForClientLocation,
  clientLocationId,
  selectedClientLocationAsset,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  clientLocationId: number;
  getAssetsForClientLocation?: (assetId: number) => Promise<void> | undefined;
  getDeliveryItems?: () => Promise<void> | undefined;
  selectedClientLocationAsset: ClientLocationAsset | null;
}) {
  const dispatch = useAppDispatch();
  const { equipmentTypes, inventoryTypes } = useAppSelector(
    (state) => state.commonFields
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const [formData, setFormData] = useState<ClientLocationAsset>({
    id: 0,
    clientLocationId: clientLocationId,
    number: null,
    equipmentTypeId: null,
    fuelInventoryTypeId: null,
    defService: false,
    imageUrl: null,
    notes: null,

    equipmentType: null,
    fuelInventoryType: null,
  });

  const getReferences = async () => {
    if (!equipmentTypes.length || !inventoryTypes.length) {
      await Promise.all([
        dispatch(fetchEquipmentTypes()),
        dispatch(fetchInventoryTypes()),
      ]);
    }
  };

  useEffect(() => {
    if (show === true) {
      handleSetInitialValues();
      getReferences();
    }
  }, [show]);

  const handleSetInitialValues = () => {
    const values = selectedClientLocationAsset
      ? {
          ...selectedClientLocationAsset,
        }
      : {
          id: 0,
          clientLocationId: clientLocationId,
          number: null,
          equipmentTypeId: null,
          fuelInventoryTypeId: null,
          defService: false,
          imageUrl: null,
          notes: null,

          equipmentType: null,
          fuelInventoryType: null,
        };
    setFormData(values as ClientLocationAsset);
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [selectedClientLocationAsset]);

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      clientLocationId: clientLocationId,
      number: null,
      equipmentTypeId: null,
      fuelInventoryTypeId: null,
      defService: false,
      imageUrl: null,
      notes: null,

      equipmentType: null,
      fuelInventoryType: null,
    };
    setFormData(values as ClientLocationAsset);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'defService') {
      setFormData((prevData) => ({ ...prevData, [name]: value === 'true' }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };
  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setButtonDisabled(true);
    setIsLoading(true);
    return await ClientApi.addUpdateClientLocationAsset(formData)
      .then((res) => {
        if (res.data.success) {
          toast.success('Save Success');
          setIsLoading(false);
          setButtonDisabled(false);
          if (getAssetsForClientLocation)
            getAssetsForClientLocation(+res.data.affectedEntityIdentifier);
          if (getDeliveryItems) getDeliveryItems();

          onCloseModal();
        } else {
          toast.error(res.data.message);
          setIsLoading(false);
          setButtonDisabled(false);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error: ' + err);
        setIsLoading(false);
        setButtonDisabled(false);
      });
  };

  function onCloseModal() {
    handleResetFormValues();
    setShow(false);
  }

  return (
    <Modal show={show} size='2xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='w-full'>
            <div className='mx-1 flex items-end justify-center'>
              <div className='flex flex-1 justify-center'>
                <h1 className='text-gray-900 font-bold text-4xl'>
                  {selectedClientLocationAsset ? 'Edit' : 'Add'} Location Asset
                </h1>
              </div>
            </div>
          </div>

          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form id='clientLocationAssetForm' onSubmit={onSubmit}>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Name/Number:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='number'
                              name='number'
                              type='text'
                              placeholder='Name/Number...'
                              onChange={handleChangeInput}
                              value={formData.number ?? ''}
                              sizing='md'
                              required
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Equipment Type:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='equipmentTypeId'
                              name='equipmentTypeId'
                              onChange={handleChangeSelect}
                              value={formData.equipmentTypeId ?? ''}
                              sizing='md'
                            >
                              <option value={''}>(Select)</option>
                              {[...equipmentTypes]
                                .sort((a, b) =>
                                  (a.name ?? '').localeCompare(b.name ?? '')
                                )
                                .map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.name}
                                  </option>
                                ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Fuel Type:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='fuelInventoryTypeId'
                              name='fuelInventoryTypeId'
                              onChange={handleChangeSelect}
                              value={formData.fuelInventoryTypeId ?? ''}
                              sizing='md'
                              required
                            >
                              <option value={''}>(Select)</option>
                              {[...inventoryTypes]
                                .sort((a, b) =>
                                  (a.name ?? '').localeCompare(b.name ?? '')
                                )
                                .map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.name}
                                  </option>
                                ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              DEF?:
                            </div>
                          </div>
                          <div className='w-full'>
                            <div className='flex items-center gap-2'>
                              <Radio
                                id='defService'
                                name='defService'
                                value={'true'}
                                checked={formData.defService ?? false}
                                onChange={handleChangeInput}
                              />
                              <div className='font-bold content-center text-sm'>
                                Yes
                              </div>
                            </div>
                            <div className='flex items-center gap-2'>
                              <Radio
                                id='defService'
                                name='defService'
                                value={'false'}
                                checked={!(formData.defService ?? false)}
                                onChange={handleChangeInput}
                              />
                              <div className='font-bold content-center text-sm'>
                                No
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Notes:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='notes'
                              name='notes'
                              type='text'
                              placeholder='Notes...'
                              onChange={handleChangeInput}
                              value={formData.notes ?? ''}
                              sizing='md'
                              autoComplete='new-password'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          form='clientLocationAssetForm'
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
